<template>
  <div class="migration-management p-4">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2>
        <i class="fas fa-database me-2"></i>
        Database Migration Management
      </h2>
      <button class="btn btn-primary" @click="refreshStatus">
        <i class="fas fa-sync-alt me-2"></i>
        Refresh
      </button>
    </div>

    <!-- Status Summary Card -->
    <div class="card mb-4" v-if="statusSummary">
      <div class="card-header bg-primary text-white">
        <h5 class="mb-0">Migration Status Summary</h5>
      </div>
      <div class="card-body">
        <div class="row">
          <div class="col-md-3">
            <div class="stat-card">
              <div class="stat-label">Total Migrations</div>
              <div class="stat-value">{{ statusSummary.totalMigrations }}</div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="stat-card">
              <div class="stat-label">Applied</div>
              <div class="stat-value text-success">{{ statusSummary.appliedMigrations }}</div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="stat-card">
              <div class="stat-label">Pending</div>
              <div class="stat-value text-warning">{{ statusSummary.pendingMigrations }}</div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="stat-card">
              <div class="stat-label">Active Tenants</div>
              <div class="stat-value text-info">{{ statusSummary.activeTenants }}</div>
            </div>
          </div>
        </div>
        <div class="mt-3">
          <small class="text-muted">
            Latest Applied: {{ statusSummary.latestAppliedVersion || 'None' }} | 
            Latest Available: {{ statusSummary.latestMigrationVersion || 'None' }}
          </small>
        </div>
      </div>
    </div>

    <!-- Pending Migrations -->
    <div class="card mb-4" v-if="pendingMigrations.length > 0">
      <div class="card-header bg-warning">
        <h5 class="mb-0">
          <i class="fas fa-clock me-2"></i>
          Pending Migrations ({{ pendingMigrations.length }})
        </h5>
      </div>
      <div class="card-body">
        <div class="alert alert-warning">
          <i class="fas fa-exclamation-triangle me-2"></i>
          These migrations have not been applied yet. Review carefully before executing.
        </div>
        <div class="table-responsive">
          <table class="table table-striped">
            <thead>
              <tr>
                <th>Version</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="migration in pendingMigrations" :key="migration.version">
                <td><code>{{ migration.version }}</code></td>
                <td>{{ migration.description }}</td>
                <td>
                  <button 
                    class="btn btn-sm btn-success me-2" 
                    @click="executeMigration(migration.version)"
                    :disabled="executing">
                    <i class="fas fa-play me-1"></i>
                    Execute
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <button 
          class="btn btn-primary mt-3" 
          @click="executeAllPending"
          :disabled="executing">
          <i class="fas fa-forward me-2"></i>
          Execute All Pending Migrations
        </button>
      </div>
    </div>

    <!-- Applied Migrations -->
    <div class="card">
      <div class="card-header bg-success text-white">
        <h5 class="mb-0">
          <i class="fas fa-check me-2"></i>
          Applied Migrations ({{ appliedMigrations.length }})
        </h5>
      </div>
      <div class="card-body">
        <div class="table-responsive">
          <table class="table table-striped">
            <thead>
              <tr>
                <th>Version</th>
                <th>Description</th>
                <th>Applied On</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="migration in appliedMigrations" :key="migration.version">
                <td><code>{{ migration.version }}</code></td>
                <td>{{ migration.description }}</td>
                <td>{{ formatDate(migration.appliedOn) }}</td>
                <td>
                  <button 
                    class="btn btn-sm btn-danger" 
                    @click="confirmRollback(migration.version)"
                    :disabled="executing">
                    <i class="fas fa-undo me-1"></i>
                    Rollback
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Execution Results Modal -->
    <div class="modal" :class="{ 'show d-block': showResultsModal }" v-if="showResultsModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Migration Execution Results</h5>
            <button type="button" class="btn-close" @click="closeResultsModal"></button>
          </div>
          <div class="modal-body">
            <div v-for="(result, index) in executionResults" :key="index" 
                 class="alert mb-2"
                 :class="result.success ? 'alert-success' : 'alert-danger'">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <strong>Version {{ result.version }}</strong>
                  <span v-if="result.schemaName"> on {{ result.schemaName }}</span>
                  <p class="mb-0 mt-1">{{ result.message }}</p>
                  <small v-if="result.duration">
                    Duration: {{ formatDuration(result.duration) }}
                  </small>
                </div>
                <i :class="result.success ? 'fas fa-check-circle text-success' : 'fas fa-times-circle text-danger'"></i>
              </div>
              <div v-if="result.errorDetails" class="mt-2">
                <details>
                  <summary>Error Details</summary>
                  <pre class="mt-2 small">{{ result.errorDetails }}</pre>
                </details>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeResultsModal">Close</button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show" v-if="showResultsModal"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import Api from '@/services/Api';

const statusSummary = ref<any>(null);
const allMigrations = ref<any[]>([]);
const appliedMigrations = ref<any[]>([]);
const pendingMigrations = ref<any[]>([]);
const executing = ref(false);
const showResultsModal = ref(false);
const executionResults = ref<any[]>([]);

const refreshStatus = async () => {
  try {
    // Get status summary
    const statusResponse = await Api.get('/api/admin/migrations/status');
    statusSummary.value = statusResponse.data;

    // Get all migrations
    const allResponse = await Api.get('/api/admin/migrations/all');
    allMigrations.value = allResponse.data;

    // Get applied migrations
    const appliedResponse = await Api.get('/api/admin/migrations/applied');
    appliedMigrations.value = appliedResponse.data.sort((a: any, b: any) => b.version - a.version);

    // Get pending migrations
    const pendingResponse = await Api.get('/api/admin/migrations/pending');
    pendingMigrations.value = pendingResponse.data.sort((a: any, b: any) => a.version - b.version);
  } catch (error: any) {
    console.error('Error refreshing migration status:', error);
    alert('Error loading migration status: ' + (error.response?.data?.error || error.message));
  }
};

const executeMigration = async (version: number) => {
  if (!confirm(`Execute migration ${version}? This will run on all active tenant schemas.`)) {
    return;
  }

  executing.value = true;
  try {
    const response = await Api.post(`/api/admin/migrations/execute/${version}`);
    executionResults.value = [response.data];
    showResultsModal.value = true;
    await refreshStatus();
  } catch (error: any) {
    console.error('Error executing migration:', error);
    alert('Error executing migration: ' + (error.response?.data?.error || error.message));
  } finally {
    executing.value = false;
  }
};

const executeAllPending = async () => {
  if (!confirm(`Execute ALL ${pendingMigrations.value.length} pending migrations? This is a significant operation that will run on all active tenant schemas.`)) {
    return;
  }

  executing.value = true;
  try {
    const response = await Api.post('/api/admin/migrations/execute-all');
    executionResults.value = response.data;
    showResultsModal.value = true;
    await refreshStatus();
  } catch (error: any) {
    console.error('Error executing all migrations:', error);
    const errorData = error.response?.data;
    if (errorData && errorData.results) {
      executionResults.value = errorData.results;
      showResultsModal.value = true;
    } else {
      alert('Error executing migrations: ' + (errorData?.error || error.message));
    }
  } finally {
    executing.value = false;
  }
};

const confirmRollback = async (version: number) => {
  if (!confirm(`⚠️ WARNING: Rollback migration ${version}? This is a destructive operation that cannot be undone easily!`)) {
    return;
  }

  if (!confirm('Are you absolutely sure? This will affect all tenant schemas.')) {
    return;
  }

  executing.value = true;
  try {
    const response = await Api.post(`/api/admin/migrations/rollback/${version}`);
    executionResults.value = [response.data];
    showResultsModal.value = true;
    await refreshStatus();
  } catch (error: any) {
    console.error('Error rolling back migration:', error);
    alert('Error rolling back migration: ' + (error.response?.data?.error || error.message));
  } finally {
    executing.value = false;
  }
};

const closeResultsModal = () => {
  showResultsModal.value = false;
  executionResults.value = [];
};

const formatDate = (dateString: string) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString();
};

const formatDuration = (duration: any) => {
  if (typeof duration === 'string') {
    return duration;
  }
  // Duration is likely a TimeSpan object
  return `${duration.totalSeconds?.toFixed(2) || '0'}s`;
};

onMounted(() => {
  refreshStatus();
});
</script>

<style scoped>
.migration-management {
  max-width: 1400px;
  margin: 0 auto;
}

.stat-card {
  text-align: center;
  padding: 1rem;
}

.stat-label {
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.5rem;
}

.stat-value {
  font-size: 2rem;
  font-weight: bold;
}

.table code {
  background-color: #f8f9fa;
  padding: 0.2rem 0.4rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
}

.modal.show {
  background-color: rgba(0, 0, 0, 0.5);
}

details {
  cursor: pointer;
}

details summary {
  color: #0d6efd;
}

details pre {
  background-color: #f8f9fa;
  padding: 0.5rem;
  border-radius: 0.25rem;
  overflow-x: auto;
}
</style>
